import { useCallback, useState } from 'react';

import { useEntitySchema, useStack9 } from '@april9au/stack9-react';
import {
  ColumnConfiguration,
  ColumnConfigurationRenderAs,
  ep,
} from '@april9au/stack9-sdk';
import * as ui from '@april9au/stack9-ui';

import { entityNames } from '../../constants/entities';
import { Industry } from '../../models/stack9/Industry';
import { ListTable } from '../ListTable/ListTable';
import { SecondaryIndustryDrawer } from '../SecondaryIndustryDrawer/SecondaryIndustryDrawer';

const { useScreen } = ui;
const { useForm } = ui.S9Form;
const S9Drawer = ui.S9Drawer.default;
const S9Form = ui.S9Form.default;
const S9Fieldset = ui.S9Fieldset.default;
const S9EntityActions = ui.S9EntityActions.default;
const Button = ui.S9Button.default;
const { Space, useRefreshList } = ui;

type IndustryFormProps = {
  id: number;
  onClose: () => void;
  initialValues?: Partial<Industry>;
};

export const IndustryForm = ({
  id,
  onClose,
  initialValues,
}: IndustryFormProps) => {
  const [form] = useForm();
  const { screen } = useScreen();
  const screenKey = ep(screen && screen.head.key);
  const columns: ColumnConfiguration[] = [
    {
      label: 'Code',
      field: 'code',
      renderAs: ColumnConfigurationRenderAs.CustomComponent,
      value: '{{code}}',
      options: {
        customComponentName: 'SecondaryIndustryDrawerAction',
      },
    },
    {
      label: 'Name',
      field: 'name',
      renderAs: ColumnConfigurationRenderAs.Text,
      value: '{{name}}',
    },
    {
      label: 'Is active',
      field: 'is_active',
      renderAs: ColumnConfigurationRenderAs.EnumTags,
      value: '{{is_active}}',
      options: {
        enumColor: {
          Yes: 'green',
          No: 'red',
        },
      },
    },
  ];

  const formFields = [
    { field: 'code', colSize: 8 },
    { field: 'name', colSize: 8 },
    { field: 'is_active', colSize: 8 },
    { field: 'lang_set', colSize: 24 },
  ];
  const { queryService } = useStack9();
  const { schema, isValidating } = useEntitySchema(entityNames.INDUSTRY);
  const [isOpen, setIsOpen] = useState(false);
  const refresh = useRefreshList();
  const handleSave = useCallback(
    async (values: Industry) => {
      await queryService.runNamedQuery(screenKey, 'updateIndustry', {
        vars: {
          code: values.code,
          name: values.name,
          is_active: values.is_active ?? null,
          lang_set: JSON.stringify(values.lang_set) ?? null,
          id,
        },
      });
      onClose?.();
      refresh();
    },
    [id, onClose, queryService, refresh, screenKey],
  );

  return (
    <S9Form
      isValidating={isValidating}
      onFinish={handleSave}
      loading={isValidating}
      form={form}
      initialValues={initialValues}
    >
      <S9Drawer.Header
        title="Edit industry"
        showCancel
        extra={
          <S9EntityActions
            showDelete
            onDeleted={onClose}
            entityKey={entityNames.INDUSTRY}
            entityId={id}
            form={form}
          />
        }
      />
      <S9Drawer.Content>
        {schema && (
          <>
            <S9Fieldset
              context={{ id: undefined }}
              schema={schema}
              formFields={formFields}
            />
            <ListTable
              queryVars={{ industryId: id }}
              queryName="getsecondaryindustriesbyindustry"
              title="Secondary industries"
              columns={columns}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'end',
                  marginBottom: '15px',
                }}
              >
                <SecondaryIndustryDrawer
                  initialValues={{ industry_id: id }}
                  open={isOpen}
                  onClose={() => {
                    setIsOpen(false);
                    refresh();
                  }}
                />
                <Space>
                  <Button
                    label="Create"
                    onClick={() => setIsOpen(true)}
                    type="primary"
                  />
                </Space>
              </div>
            </ListTable>
          </>
        )}
      </S9Drawer.Content>
    </S9Form>
  );
};
